import { reflectKeys } from "@utils/key.reflactor.ts";
import { RESOURCE_DOMAIN_PATH } from "@plugins/common/constant/resource.constant";

export const NAMESPACE = {
  APP: "app",
  AUTH: "auth",
  EDITOR: "editor",
  LESSON: "editor/lesson",
  COMPONENT: "editor/component",
};

export const TEMPLATE_TYPE = reflectKeys(["LESSON", "ACTIVITY"]);

export const DEFAULT_LEARNING_TYPE = "CUSTOM";

export const RESOURCE_TAGGING_RESOURCE_TYPE = reflectKeys([
  "LESSON",
  "LESSON_ACTIVITY",
  "QUIZ",
  "QUESTION",
  "RUBRIC",
]);

export const COURSE_CREATION_OPTION = reflectKeys([
  "CREATE_BLANK_ACTIVITY",
  "CREATE_BLANK_QUIZ",
  "CREATE_LESSON_COVER",
  "CREATE_SECTION_COVER",
  "CREATE_ADAPTIVE_SECTION_COVER",
]) as {
  CREATE_BLANK_ACTIVITY: string;
  CREATE_BLANK_QUIZ: string;
  CREATE_LESSON_COVER: string;
  CREATE_SECTION_COVER: string;
  CREATE_ADAPTIVE_SECTION_COVER: string;
};

export const LESSON_ACTIVITY_COMPONENT_TYPE = reflectKeys([
  "CONTAINER_SINGLE_COLUMN",
  "CONTAINER_TWO_COLUMN",
  "CONTAINER_THREE_COLUMN",
  "CONTAINER_ACCORDION",
  "CONTAINER_ACCORDION_ITEM",
  "COMPONENT_QUESTION",
  "COMPONENT_RICHTEXT",
  "COMPONENT_POLL",
  "COMPONENT_TOPIC",
  "ACTIVITY_QUIZ",
]);

export const LESSON_ACTIVITY_CONTAINER_TYPE = reflectKeys([
  "CONTAINER_SINGLE_COLUMN",
  "CONTAINER_TWO_COLUMN",
  "CONTAINER_THREE_COLUMN",
  "CONTAINER_ACCORDION",
  "CONTAINER_ACCORDION_ITEM",
]);

export const QUESTION_TYPE = reflectKeys([
  "MCQ",
  "MRQ",
  "MPQ",
  "REC",
  "FREE",
  "FILE",
  "FILL",
  "DND",
  "EEQ",
]);

export const OPTION_DISPLAY_MODE = {
  MCQ: { value: "MCQ", label: "poll_type_mcq_label" },
  SLIDER: { value: "SLIDER", label: "poll_type_slider_label" },
  TEXT_INPUT: { value: "TEXTINPUT", label: "option_display_type_text_input" },
  SHARED_DROPDOWN: { value: "SHARED_DROPDOWN", label: "option_display_type_shared_dropdown" },
  DROPDOWN: { value: "DROPDOWN", label: "option_display_type_dropdown" },
};

export const ADD_COMPONENT_TYPE = reflectKeys([
  "LESSON",
  "QUESTION",
  "RUBRIC",
  "MEDIA",
  "IMAGE",
  "QUIZ",
]);

export const ADD_RUBRIC_FROM_TYPE = reflectKeys(["ACTIVITY_QUIZ", "QUESTION"]);

export const ADD_FROM_EXISTING_TYPE = reflectKeys([
  "ACTIVITY",
  "QUESTION",
  "RUBRIC",
  "MEDIA",
  "THUMBNAIL",
  "GAME_BOOK",
]);

export const STARRED_RESOURCES_PAGE_SIZE = 12;

export const ADD_FROM_EXISTING_LOCATION = reflectKeys(["LIBRARY", "STARRED_RESOURCES"]) as {
  LIBRARY: string;
  STARRED_RESOURCES: string;
};

export const ADD_FROM_LIBRARY_SEARCH_MODE = reflectKeys([
  "VIEW",
  "SINGLE_SELECTION",
  "MULTI_SELECTION",
  "MULTI_SELECTION_COMPONENT",
  "CALENDAR_SELECT",
]) as {
  VIEW: string;
  SINGLE_SELECTION: string;
  MULTI_SELECTION: string;
  MULTI_SELECTION_COMPONENT: string;
  CALENDAR_SELECT: string;
};

export const ADD_FROM_EMBED_WEBSITE_SEARCH_MODE = reflectKeys(["VIEW", "SINGLE_SELECTION"]);

export const EDITOR_MODE = reflectKeys([
  "EDIT", // To remove
  "VIEW", //retain
  "ACTIVE_PREEDIT", //retain
  "INACTIVE_PREEDIT",
  "ACTIVE_EDIT", //retain
  "INACTIVE_EDIT",
]);

export const EDITOR_SAVING_EVENTBUS_EVENT = {
  COMPONENT_BLUR: "COMPONENT_BLUR",
};

export const EDITOR_ACTIVITY_PAGE_NAVIGATION_EVENTBUS_EVENT = {
  NAVIGATE_TO_PAGE: "NAVIGATE_TO_PAGE",
};

export const EDITOR_QUIZ_ACTIVITY_PAGE_NAVIGATION_EVENTBUS_EVENT = {
  NAVIGATE_TO_PAGE: "NAVIGATE_TO_QUIZ_PAGE",
};

export const EDITOR_SAVING_STATE = {
  VIEW: "VIEW",
  SAVING: "SAVING",
  SAVED: "SAVED",
  VALIDATION_FAIL: "VALIDATION_FAIL",
  UNKNOWN_FAILURE: "UNKNOWN_FAILURE",
};
export const EDITOR_SAVING_PROMISE_RULE = {
  COMPONENT_SAVE: "COMPONENT_SAVE",
  RESOURCE_SAVE: "RESOURCE_SAVE",
  COMPONENT_REMOVE: "COMPONENT_REMOVE", // new component bar
};

export const EDITOR_FIELD_DEFAULT_VALUES = {
  ACTIVITY_TITLE: "Untitled",
  LESSON_TITLE: "Untitled",
  SECTION_TITLE: "", // allow to be empty
  GAMIFICATION_TITLE: "Untitled Game",
  GAME_STORY_TITLE: "Untitled Game Story",
  ACHIEVEMENT_TITLE: "Untitled Collectible",
  GAME_AVATAR_TITLE: "Untitled Collectible",
  DIGITAL_BADGE_TITLE: "Untitled Collectible",
};

export const VALIDATION = {
  MAX_ACTIVITY: 20,
  MAX_CHILD_QUESTION: 26,
  MAX_TOTAL_SUB_QUESTION: 40,
  MAX_ACTIVITIES: 20,
  MAX_PAGES: 100,
  MAX_SECTION: 20,
  MAX_OPTION: 26,
  MAX_SLIDER_OPTION: 7,
  MAX_COMPONENTS: 100,
  MAX_LENGTH_TITLE: 250,
  MAX_HINT: 3,
  MAX_FITB_BLANKS: 100, // max blanks for each fitb question
  MAX_FITB_OPTIONS_QUESTION: 100, // max options for each fitb question
  MAX_FITB_OPTIONS_INDIVIDUAL: 20, // max options for each fitb blank or group
  MAX_FITB_GROUPS: 10,
  MAX_FITB_BLANKS_PER_GROUP: 15,
  FITB_ANSWER_CHAR_LIMIT: 500,
  MAX_FITB_DISTRACTOR_CHAR_LIMIT: 500,
  MIN_QUIZ_RECOMMENDED_TIME: 300,
};

export const ALPHABETS_LIST = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
];

export const EVALUATION_LANGUAGE = {
  NONE: { value: "", label: "speech_evaluation_default_evaluation_language_label" },
  ZH: { value: "ZH", label: "speech_evaluation_chinese_evaluation_language_label" },
};

export const INTEGRATOR_APP_DROPDOWN_OPTIONS = {
  DEFAULT: "Choose an option",
};

export const QUIZ_ACTIVITY_COMPONENT = {
  QUIZ_HEADER: "QUIZ_HEADER",
  QUIZ_COVER: "QUIZ_COVER",
};

export const QUIZ_TITLE_CHARACTER_LIMIT = 250;

export const QUIZ_PAGE = {
  QUIZ_COVER: "cover",
  QUIZ_END: "end",
};

export const QUIZ_TEST_MODE = reflectKeys([
  "PRACTICE",
  "TEST_AUTO_RELEASE",
  "TEST_TEACHER_RELEASE",
]);

// TODO: don't store display name in constant, use locale. how does this going to work if we support languages?
export const QUIZ_TYPE = {
  PROGRESSIVE: "Progressive Quiz",
  AUTO: "Auto-Graded Quiz",
  TEACHER_MARKED: "Teacher-Marked Quiz",
};

// TODO: don't store display name in constant, use locale. how does this going to work if we support languages?
export const QUIZ_TEST_MODE_TYPE = {
  PRACTICE: {
    name: "PROGRESSIVE",
    displayName: "Progressive Quiz",
  },
  TEST_AUTO_RELEASE: {
    name: "AUTO",
    displayName: "Auto-Graded Quiz",
  },
  TEST_TEACHER_RELEASE: {
    name: "TEACHER_MARKED",
    displayName: "Teacher-Marked Quiz",
  },
};

export const QUIZ_EVENT = {
  QUIZ_PAGE_UPDATE: "quizPageUpdate",
  QUIZ_QUESTION_MOVE_LEFT: "moveLeft",
  QUIZ_QUESTION_MOVE_RIGHT: "moveRight",
} as const;

export const ACTIVITY_EVENT = {
  ACTIVITY_PAGE_UPDATE: "activityPageUpdate",
  ACTIVITY_QUESTION_MOVE_LEFT: "moveLeft",
  ACTIVITY_QUESTION_MOVE_RIGHT: "moveRight",
};

export const QUERY_PARAM = {
  QUIZ_PAGE: "quizPage",
  TEACHER_REVIEW: "teacherReview",
};

export const NON_CLICKABLE_ANCHOR_ELEMENTS = ["cv-button", "cv-overflow-menu"];

export const COMPONENT_ATTR = {
  DATA_LOADED: "data-loaded",
};

export const LESSON_SUBMIT_TARGET = reflectKeys(["ASSIGN", "MOE", "CG"]);

export const RESOURCE_TYPE = reflectKeys(["LESSON", "QUIZ", "QUESTION", "RUBRIC", "MEDIA"]);

export const RESOURCE_STATUS = reflectKeys([
  "DRAFT",
  "UNPUBLISHED",
  "PENDING_APPROVAL",
  "APPROVED",
  "REJECTED",
  "ARCHIVED",
]);

export const MEDIA_TYPE = reflectKeys([
  "IMAGE",
  "VIDEO",
  "HTML5",
  "AUDIO",
  "POWERPOINT",
  "WORD",
  "EXCEL",
  "PDF",
  "ZIP",
  "OTHERS",
]);

export const RESOURCE_CARD_TYPE = reflectKeys(["LESSON", "MEDIA", "QUIZ", "QUESTION", "FILE"]);

//used in addActivityContentSubpage
export const ACTIVITY_TYPE_SELECTION = {
  BLANK: "blankActivity",
  PRACTICE_QUIZ: "practiceQuiz",
  AUTO_QUIZ: "autoReleaseQuiz",
  TEACHER_QUIZ: "manualReleaseQuiz",
  FROM_EXISTING: "fromExisting",
  DUPLICATE: "duplicate",
};

export const THUMBNAIL_RESOURCE_TYPE = {
  LESSON: "lesson",
  SECTION: "section",
};

export const LESSON_PERMISSIONS = {
  DUPLICATABLE: "duplicatable",
  PRINTABLE: "printable",
  PRINTABLE_ANSWERS: "printableAnswers",
  REATTEMPTABLE: "reattemptable",
  VISIBLE_BY_STUDENT: "visibleByStudent", // i.e. NonAssociatedStudent in BE
  VISIBLE_BY_TEACHER: "visibleByTeacher",
  VISIBLE_BY_TEACHER_STUDENT: "visibleByTeacherStudent", // i.e. AssociatedStudent in BE
};

export const LESSON_SETTINGS = {
  HIDE_SECTION_ACTIVITY_NUMBER: "hideSectionActivityNumber",
};

export const STUDENT_GROUP_RESOURCE_TYPE = {
  ASSIGNMENT: "ASSIGNMENT",
  CLASS_GROUP_RESOURCE: "CLASS_GROUP_RESOURCE",
};

export const ALP_STAGE_VALUES = {
  CUSTOM: {
    name: "CUSTOM",
    displayName: "lesson_template_label_alp_custom",
  },
  ACTIVATE: {
    name: "ACTIVATE",
    displayName: "lesson_template_label_alp_activate",
  },
  PROMOTE: {
    name: "PROMOTE",
    displayName: "lesson_template_label_alp_promote",
  },
  FACILITATE: {
    name: "FACILITATE",
    displayName: "lesson_template_label_alp_facilitate",
  },
  MONITOR: {
    name: "MONITOR",
    displayName: "lesson_template_label_alp_monitor",
  },
};

export const RULE_CONDITION_TYPE = {
  SCHEDULE: "SCHEDULE",
  ACCESSIBLE: "ACCESSIBLE",
  GROUP_ACCESS: "GROUP_ACCESS",
  PREVIOUS_COMPLETION: "PREVIOUS_COMPLETION",
  RESOURCE_COMPLETION: "RESOURCE_COMPLETION",
  QUIZ_SCORE: "QUIZ_SCORE",
  GAME_AWARD: "GAME_AWARD",
  GAME_CHOICE: "GAME_CHOICE",
  LEVEL_UP: "LEVEL_UP",
  QUIZ_RANK: "QUIZ_RANK",
};

export const GRADING_RUBRIC_DUPLICATE_STATUS = reflectKeys(["PROCESSING", "READY"]);

export const LAC_STATUS = reflectKeys(["PROCESSING", "READY", "ERROR"]);

export const LAC_ERROR_REASON = reflectKeys(["RESOURCE_NOT_FOUND"]);

export const QTI_UPLOAD_STATUS = {
  SCANNING: "SCANNING",
  UPLOADING: "UPLOADING",
  READY: "READY",
  ERROR: "ERROR",
};

export const QTI_MODE = {
  NONE: "NONE",
  SEPARATE_PAGE: "SEPARATE_PAGE",
  TEAM_RESPONSE: "TEAM_RESPONSE",
};

export const DEFAULT_TEMPLATE_PAGE_SIZE = {
  CAROUSEL: 9,
  FULL: 12,
  MY_DRIVE: 20,
};

export const COMPONENT_BAR_TYPE = {
  COPILOT_GENERATE: {
    key: "COPILOT_GENERATE",
    icon: "ACPGenerate32",
    iconKind: "indicative",
    disabled: false,
    label: "component_bar_type_acp_generate",
    type: "main",
  },
  TEXT_MEDIA: {
    key: "TEXT_MEDIA",
    icon: "TextMedia32",
    iconKind: "indicative",
    disabled: false,
    label: "component_bar_type_text_media",
    type: "main",
  },
  QUESTION: {
    key: "QUESTION",
    icon: "Question32",
    iconKind: "indicative",
    disabled: false,
    label: "component_bar_type_question",
    type: "main",
  },
  INTERACTIVE: {
    key: "INTERACTIVE",
    icon: "Collaborative32",
    iconKind: "indicative",
    disabled: false,
    label: "component_bar_type_interactive",
    type: "main",
  },
  DISPLAY: {
    key: "DISPLAY",
    icon: "2Columns32",
    iconKind: "indicative",
    disabled: false,
    label: "component_bar_type_display",
    type: "main",
  },
};

export const COMPONENT_BAR_SUB_TYPE = {
  SPLIT_PAGE_MEDIA: {
    key: "SPLIT_PAGE_MEDIA",
    icon: "PaginatedMedia16",
    iconKind: "indicative",
    hidden: false,
    label: "component_bar_subtype_split_page_media",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.TEXT_MEDIA.key,
  },
  TEXT: {
    key: "TEXT",
    icon: "Text16",
    iconKind: "indicative",
    hidden: false,
    label: "component_bar_subtype_text",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.TEXT_MEDIA.key,
  },
  FILE_FROM_DEVICE: {
    key: "FILE_FROM_DEVICE",
    icon: "Attachment16",
    iconKind: "indicative",
    hidden: false,
    label: "component_bar_subtype_file_from_device",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.TEXT_MEDIA.key,
  },
  GOOGLE_FILE: {
    key: "GOOGLE_FILE",
    icon: "Google16",
    iconKind: "indicative",
    hidden: true,
    label: "component_bar_subtype_google_file",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.TEXT_MEDIA.key,
  },
  YOUTUBE: {
    key: "YOUTUBE",
    icon: "Video16",
    iconKind: "indicative",
    hidden: false,
    label: "component_bar_subtype_youtube",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.TEXT_MEDIA.key,
  },
  APP: {
    key: "APP",
    icon: "App16",
    iconKind: "indicative",
    hidden: false,
    label: "component_bar_subtype_app",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.TEXT_MEDIA.key,
  },
  WEBSITE: {
    key: "WEBSITE",
    icon: "Website16",
    iconKind: "indicative",
    hidden: false,
    label: "component_bar_subtype_web",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.TEXT_MEDIA.key,
  },
  ADD_EXISTING: {
    key: "ADD_EXISTING",
    icon: "Plus16",
    iconKind: "action",
    hidden: false,
    label: "component_bar_type_text_add_from_existing",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.QUESTION.key,
  },
  MCQ: {
    key: "MCQ",
    icon: "MCQ16",
    iconKind: "action",
    hidden: false,
    label: "activity_add_component_button_mcq_component_label",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.QUESTION.key,
  },
  FILL: {
    key: "FILL",
    icon: "FillInTheBlanks16",
    iconKind: "action",
    hidden: false,
    label: "activity_add_component_button_fitb_component_label",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.QUESTION.key,
  },
  CNDQ: {
    key: "CNDQ",
    icon: "ClickandDrop16",
    iconKind: "action",
    hidden: false,
    label: "activity_add_component_button_cnd_component_label",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.QUESTION.key,
  },
  EERQ: {
    key: "EERQ",
    icon: "ErrorEditing16",
    iconKind: "action",
    hidden: false,
    label: "activity_add_component_button_eer_component_label",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.QUESTION.key,
  },
  FRQ: {
    key: "FRQ",
    icon: "RichText16",
    iconKind: "action",
    hidden: false,
    label: "activity_add_component_button_frq_component_label",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.QUESTION.key,
  },
  ARQ: {
    key: "ARQ",
    icon: "Audio16",
    iconKind: "action",
    hidden: false,
    label: "activity_add_component_button_arq_component_label",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.QUESTION.key,
  },
  MPQ: {
    key: "MPQ",
    icon: "MPQ16",
    iconKind: "action",
    hidden: false,
    label: "activity_add_component_button_mpq_component_label",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.QUESTION.key,
  },
  ISR: {
    key: "ISR",
    icon: "App16",
    iconKind: "action",
    hidden: true,
    label: "activity_add_component_button_isr_component_label",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.QUESTION.key,
  },
  POLL: {
    key: "POLL",
    icon: "Poll16",
    iconKind: "indicative",
    hidden: false,
    label: "activity_add_component_button_poll_component_label",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.INTERACTIVE.key,
  },
  TOPIC: {
    key: "TOPIC",
    icon: "Discussion16",
    iconKind: "indicative",
    hidden: false,
    label: "activity_add_component_button_LAC_topic_component_label",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.INTERACTIVE.key,
  },
  TOPIC_LA: {
    key: "TOPIC_LA",
    icon: "LearningAssistant16",
    iconKind: "indicative",
    hidden: true,
    label: "activity_add_component_button_la_component_label",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.INTERACTIVE.key,
  },
  PAGES: {
    key: "PAGES",
    icon: "Pagination16",
    iconKind: "indicative",
    hidden: false,
    label: "activity_add_container_button_page_break_label",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.DISPLAY.key,
  },
  TWO_COLUMN: {
    key: "TWO_COLUMN",
    icon: "2Columns16",
    iconKind: "indicative",
    hidden: false,
    label: "two_columns_component_settings_label",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.DISPLAY.key,
  },
  THREE_COLUMN: {
    key: "THREE_COLUMN",
    icon: "3Columns16",
    iconKind: "indicative",
    hidden: false,
    label: "three_columns_component_settings_label",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.DISPLAY.key,
  },
  ACCORDION: {
    key: "ACCORDION",
    icon: "Accordion16",
    iconKind: "indicative",
    hidden: false,
    label: "accordion_component_settings_label",
    type: "sub",
    parent: COMPONENT_BAR_TYPE.DISPLAY.key,
  },
};

export const COMPONENT_BAR_SUB_TYPE_OPTIONS = {
  ADD_FILE: {
    key: "ADD_FILE",
    hidden: false,
    label: "component_bar_subtype_option_google_add_file",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.GOOGLE_FILE.key,
  },
  EMBED_FILE: {
    key: "EMBED_FILE",
    hidden: false,
    label: "component_bar_subtype_option_google_embed_file",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.GOOGLE_FILE.key,
  },
  MCQ_SINGLE: {
    key: "MCQ_SINGLE",
    hidden: false,
    label: "component_bar_subtype_option_mcq_single_response",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.MCQ.key,
  },
  MCQ_MULTIPLE: {
    key: "MCQ_MULTIPLE",
    hidden: false,
    label: "component_bar_subtype_option_mcq_multi_response",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.MCQ.key,
  },
  MCQ_SLIDER: {
    key: "MCQ_SLIDER",
    hidden: false,
    label: "component_bar_subtype_option_mcq_slider_response",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.MCQ.key,
  },
  FILL_TEXT_INPUT: {
    key: "FILL_TEXT_INPUT",
    hidden: false,
    label: "component_bar_subtype_option_fitb_text_input",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.FILL.key,
  },
  FILL_SHARED_DROPDOWN: {
    key: "FILL_SHARED_DROPDOWN",
    hidden: false,
    label: "component_bar_subtype_option_fitb_shared_dropdown",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.FILL.key,
  },
  FILL_DROPDOWN: {
    key: "FILL_DROPDOWN",
    hidden: false,
    label: "component_bar_subtype_option_fitb_dropdown",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.FILL.key,
  },
  FRQ_DEFAULT: {
    key: "FRQ_DEFAULT",
    hidden: false,
    label: "component_bar_subtype_option_frq_default",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.FRQ.key,
  },
  FRQ_FILE: {
    key: "FRQ_FILE",
    hidden: false,
    label: "component_bar_subtype_option_frq_file",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.FRQ.key,
  },
  FRQ_GOOGLE: {
    key: "FRQ_GOOGLE",
    hidden: true,
    label: "component_bar_subtype_option_frq_google",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.FRQ.key,
  },
  FRQ_AFA: {
    key: "FRQ_AFA",
    hidden: true,
    label: "",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.FRQ.key,
  },
  FRQ_AK: {
    key: "FRQ_AK",
    hidden: true,
    label: "",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.FRQ.key,
  },
  FRQ_LFA_GC: {
    key: "FRQ_LFA_GC",
    hidden: true,
    label: "",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.FRQ.key,
  },
  FRQ_LFA_SA: {
    key: "FRQ_LFA_SA",
    hidden: true,
    label: "",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.FRQ.key,
  },
  FRQ_INTEGRATED_SCORABLE_RESPONSE: {
    key: "FRQ_INTEGRATED_SCORABLE_RESPONSE",
    hidden: false,
    label: "component_bar_subtype_option_frq_integrated_scorable_response",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.FRQ.key,
  },
  ARQ_DEFAULT: {
    key: "ARQ_DEFAULT",
    hidden: false,
    label: "component_bar_subtype_option_arq_default",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.ARQ.key,
  },
  ARQ_SE_ZH: {
    key: "ARQ_SE_ZH",
    hidden: true,
    label: "",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.ARQ.key,
  },
  ARQ_SE_EN: {
    key: "ARQ_SE_EN",
    hidden: true,
    label: "",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.ARQ.key,
  },
  ARQ_SE_MS: {
    key: "ARQ_SE_MS",
    hidden: true,
    label: "",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.ARQ.key,
  },
  ARQ_SE_TA: {
    key: "ARQ_SE_TA",
    hidden: true,
    label: "",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.ARQ.key,
  },
  ADD_EXISTING_FROM_LIBRARY: {
    key: "ADD_EXISTING_FROM_LIBRARY",
    hidden: false,
    label: "component_bar_subtype_option_add_from_library",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.ADD_EXISTING.key,
  },
  ADD_EXISTING_FROM_MY_DRIVE: {
    key: "ADD_EXISTING_FROM_MY_DRIVE",
    hidden: false,
    label: "component_bar_subtype_option_add_from_my_drive",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.ADD_EXISTING.key,
  },
  ADD_EXISTING_FROM_STARRED: {
    key: "ADD_EXISTING_FROM_STARRED",
    hidden: false,
    label: "component_bar_subtype_option_add_from_starred",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.ADD_EXISTING.key,
  },
  ADD_EXISTING_FROM_FILE: {
    key: "ADD_EXISTING_FROM_FILE",
    hidden: false,
    label: "component_bar_subtype_option_add_from_file",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.ADD_EXISTING.key,
  },
  POLL_SINGLE_SELECTION: {
    key: "POLL_SINGLE_SELECTION",
    hidden: false,
    label: "component_bar_subtype_option_poll_single",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.POLL.key,
  },
  POLL_MULTI_SELECTION: {
    key: "POLL_MULTI_SELECTION",
    hidden: false,
    label: "component_bar_subtype_option_poll_multi",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.POLL.key,
  },
  POLL_SLIDER: {
    key: "POLL_SLIDER",
    hidden: false,
    label: "component_bar_subtype_option_poll_slider",
    class: "component_bar_subtype_option_poll_slider_class",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.POLL.key,
  },
  SINGLE_PAGE_BREAK: {
    key: "SINGLE_PAGE_BREAK",
    hidden: false,
    label: "single_page_break_component_settings_label",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.PAGES.key,
  },
  MULTIPLE_PAGE_BREAK: {
    key: "MULTIPLE_PAGE_BREAK",
    hidden: false,
    label: "multiple_page_break_component_settings_label",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.PAGES.key,
  },
  DUPLICATE_CURRENT_PAGE: {
    key: "DUPLICATE_CURRENT_PAGE",
    hidden: false,
    label: "duplicate_current_page_component_settings_label",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.PAGES.key,
  },
  TOPIC_DEFAULT: {
    key: "TOPIC_DEFAULT",
    hidden: false,
    label: "component_bar_subtype_option_topic_default",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.TOPIC.key,
  },
  TOPIC_LIVE_CHAT: {
    key: "TOPIC_LIVE_CHAT",
    hidden: false,
    label: "conversation_live_chat_label_title",
    type: "options",
    parent: COMPONENT_BAR_SUB_TYPE.TOPIC.key,
  },
  COPILOT_GENERATE: {
    key: "COPILOT_GENERATE",
    icon: "ACPGenerate16",
    iconKind: "indicative",
    disabled: false,
    label: "component_bar_type_acp_generate",
    type: "sub",
  },
};

export const LESSON_ACTIVITY_INDICATOR_ICON = {
  INTRODUCTION: {
    VIEWABLE: "Introduction32",
    HIDDEN: "IntroductionHidden32",
    DISABLED: "IntroductionDisabled32",
  },
  END: {
    VIEWABLE: "AssignmentEndDisabled32",
    HIDDEN: "AssignmentEndHidden32",
    DISABLED: "AssignmentEndDisabled32",
    COMPLETED: "AssignmentEndCompleted32",
  },
  SECTION: {
    VIEWABLE: "Section32",
    HIDDEN: "SectionHidden32",
    DISABLED: "SectionDisabled32",
    RESTRICTED: "SectionPrereq32",
    COMPLETED: "SectionCompleted32",
  },
  SECTION_END: {
    VIEWABLE: "PageEnd32",
    HIDDEN: "PageEndHidden32",
    DISABLED: "PageEndDisabled32",
    COMPLETED: "PageEndCompleted32",
  },
};

export const EMBED_TYPE = reflectKeys(["URL", "CODE"]);

export const LESSON_TEMPLATE_TYPE = reflectKeys(["MY_TEMPLATE", "SLS_TEMPLATE"]);

export const RESPONSE_SIZES = [
  { value: "SIZE_S", label: "question_edit_response_size_short_label" },
  { value: "SIZE_M", label: "question_edit_response_size_medium_label" },
  { value: "SIZE_1", label: "question_edit_response_size_long_label" },
];

export const RESPONSE_SIZES_MAPPING = {
  SIZE_S: { sizeString: "short" },
  SIZE_M: { sizeString: "medium" },
  SIZE_1: { sizeString: "long", pageSize: 1 },
  SIZE_2: { sizeString: "long", pageSize: 2 },
  SIZE_3: { sizeString: "long", pageSize: 3 },
  SIZE_4: { sizeString: "long", pageSize: 4 },
  SIZE_5: { sizeString: "long", pageSize: 5 },
  SIZE_6: { sizeString: "long", pageSize: 6 },
  SIZE_7: { sizeString: "long", pageSize: 7 },
  SIZE_8: { sizeString: "long", pageSize: 8 },
  SIZE_9: { sizeString: "long", pageSize: 9 },
};

export const ANSWER_INPUT_PAGE_STRING = {
  1: "SIZE_1",
  2: "SIZE_2",
  3: "SIZE_3",
  4: "SIZE_4",
  5: "SIZE_5",
  6: "SIZE_6",
  7: "SIZE_7",
  8: "SIZE_8",
  9: "SIZE_9",
};

export const TOPIC_CHARACTER_LIMIT_TITLE = 250;

export const RANDOM_PAGE_COUNT_DEFAULT = {
  SYSTEM: 0,
  INPUT: 1,
  MAX: 100,
};

export const MAX_MARKS_DEFAULT = {
  SYSTEM: 0,
  INPUT: 1,
  MAX: 1000,
};

export const ADD_POWERPOINT_SLIDE_ITEM = reflectKeys(["ALL", "RANGE"]);

export const SHORT_ANSWER_LENIENCY = {
  LOW: {
    key: "low",
    text: "question_edit_short_answer_leniency_low_label",
    value: "LOW",
  },
  MEDIUM: {
    key: "medium",
    text: "question_edit_short_answer_leniency_medium_label",
    value: "MEDIUM",
  },
  HIGH: {
    key: "high",
    text: "question_edit_short_answer_leniency_high_label",
    value: "HIGH",
  },
};

export const AFA_LENIENCY = {
  LOW: {
    key: "low",
    text: "question_edit_afa_leniency_low_label",
    value: "LOW",
  },
  MEDIUM: {
    key: "medium",
    text: "question_edit_afa_leniency_medium_label",
    value: "MEDIUM",
  },
  HIGH: {
    key: "high",
    text: "question_edit_afa_leniency_high_label",
    value: "HIGH",
  },
};

export const GRAMMAR_CORRECTION_TOOLTIP_RECOMMENDED_CHAR_NO = {
  PREPOPULATED_ANSWER_ENGLISH: 1000,
  PREPOPULATED_ANSWER_CHINESE: 400,
};

export const LESSON_TEMPLATE_SUBPAGE_TAB = {
  CREATE: "createNew",
  EDIT: "editLatest",
};

export const LESSON_TEMPLATE_SUBPAGE_TAB_STORAGE_KEY = "lesson-template-tab-";

export const RESOURCE_DOMAIN = {
  MOE: { value: "MOE", path: RESOURCE_DOMAIN_PATH.MOE_LIBRARY },
  COMMUNITY: { value: "COMMUNITY", path: RESOURCE_DOMAIN_PATH.COMMUNITY_GALLERY },
  PERSONAL: { value: "PERSONAL", path: RESOURCE_DOMAIN_PATH.MY_LIBRARY },
  ALP_TEMPLATE: { value: "ALP_TEMPLATE", path: RESOURCE_DOMAIN_PATH.ALP_TEMPLATE },
  ASSIGNMENT: { value: "ASSIGNMENT", path: RESOURCE_DOMAIN_PATH.ASSIGNMENT },
};

export const AUTHORING_COPILOT_CONTENT_ITEM_TYPE = reflectKeys([
  "LESSON_DESCRIPTION",
  "SECTION_NOTES",
  "SECTION_DESCRIPTION",
  "ACTIVITY",
  "ACTIVITY_NOTES",
  "QUIZ",
  "DISCUSSION",
  "POLL",
  "TEXT",
  "FRQ",
  "MRQ",
  "EEQ",
  "FILL",
]);

export const AUTHORING_COPILOT_QUESTION_ITEM_TYPE = [
  AUTHORING_COPILOT_CONTENT_ITEM_TYPE.DISCUSSION,
  AUTHORING_COPILOT_CONTENT_ITEM_TYPE.POLL,
  AUTHORING_COPILOT_CONTENT_ITEM_TYPE.TEXT,
  AUTHORING_COPILOT_CONTENT_ITEM_TYPE.FRQ,
  AUTHORING_COPILOT_CONTENT_ITEM_TYPE.MRQ,
  AUTHORING_COPILOT_CONTENT_ITEM_TYPE.EEQ,
  AUTHORING_COPILOT_CONTENT_ITEM_TYPE.FILL,
];

export const COMPONENT_AUTHORING_COPILOT_TYPE = reflectKeys(["ACTIVITY", "QUIZ"]);

// value corresponds to the QuestionType
export const COMPONENT_AUTHORING_COPILOT_CONTENT_ITEM_TYPE = {
  discussionQuestion: {
    value: "DISCUSSION",
    typeNameValue: AUTHORING_COPILOT_CONTENT_ITEM_TYPE.DISCUSSION,
    isQuizComponent: false,
    isContainerComponent: false,
    isMpqComponent: false,
  },
  errorEditingQuestion: {
    value: "EEQ",
    typeNameValue: AUTHORING_COPILOT_CONTENT_ITEM_TYPE.EEQ,
    isQuizComponent: true,
    isContainerComponent: false,
    isMpqComponent: true,
  },
  fillInTheBlankQuestion: {
    value: "FILL",
    typeNameValue: AUTHORING_COPILOT_CONTENT_ITEM_TYPE.FILL,
    isQuizComponent: true,
    isContainerComponent: false,
    isMpqComponent: true,
  },
  freeResponseQuestion: {
    value: "FREE",
    typeNameValue: AUTHORING_COPILOT_CONTENT_ITEM_TYPE.FRQ,
    isQuizComponent: true,
    isContainerComponent: true,
    isMpqComponent: true,
  },
  multipleChoiceQuestion: {
    value: "MCQ",
    typeNameValue: AUTHORING_COPILOT_CONTENT_ITEM_TYPE.MRQ,
    isQuizComponent: true,
    isContainerComponent: true,
    isMpqComponent: true,
  },
  poll: {
    value: "POLL",
    typeNameValue: AUTHORING_COPILOT_CONTENT_ITEM_TYPE.POLL,
    isQuizComponent: false,
    isContainerComponent: false,
    isMpqComponent: false,
  },
  text: {
    value: "TEXT",
    typeNameValue: AUTHORING_COPILOT_CONTENT_ITEM_TYPE.TEXT,
    isQuizComponent: true,
    isContainerComponent: true,
    isMpqComponent: false,
  },
};

export const ADD_SPLIT_MEDIA_OPTION = reflectKeys(["FILE", "YOUTUBE"]);

export const ERROR_CODE = {
  EXCEED_MAX_NUMBER_OF_KNOWLEDGE_BASES_PER_RESOURCE:
    "exceed-max-number-of-knowledge-bases-per-resource",
};

export const EDITOR_RIGHT_PANEL = {
  GENERAL: "general",
  APPEARANCE: "appearance",
  VIEW_SWITCHER: "view-switcher",
};

export const NUMBER_INPUT_DEBOUNCE_WAIT = 500;

export const AFA_FEEDBACK_REFERENCE = reflectKeys([
  "ERROR_TAG",
  "SUGGESTED_ANSWER",
  "GRADING_RUBRIC",
]);

export const FEEDBACK_ASSISTANT_SETUP_FA = reflectKeys(["AFA", "SA"]);

export const FEEDBACK_ASSISTANT_SETUP_TABS = [
  {
    key: "SA",
    title: "Short Answer Feedback Assistant",
  },
  {
    key: "AFA",
    title: "Annotated Feedback Assistant",
  },
];

export const AFA_FEEDBACK_REFERENCE_DROPDOWN_OPTIONS = [
  {
    value: "ERROR_TAG",
    label: "afa_setup_subpage_feedback_reference_dropdown_error_tags",
    key: "ERROR_TAG",
    allowDuplicateChoice: true,
  },
  {
    value: "GRADING_RUBRIC",
    label: "afa_setup_subpage_feedback_reference_dropdown_rubric",
    key: "GRADING_RUBRIC",
    allowDuplicateChoice: false,
  },
  {
    value: "SUGGESTED_ANSWER",
    label: "afa_setup_subpage_feedback_reference_dropdown_suggested_answer",
    key: "SUGGESTED_ANSWER",
    allowDuplicateChoice: false,
  },
];

export const AFA_HIGHLIGHT_COLOR = reflectKeys(["RED", "GREEN", "BLUE"]);

export const AFA_HIGHLIGHT_COLOR_OPTIONS = [
  {
    value: "RED",
    class: "underline-01",
    key: "annotation-underline-01",
  },
  {
    value: "GREEN",
    class: "underline-02",
    key: "annotation-underline-02",
  },
  {
    value: "BLUE",
    class: "underline-03",
    key: "annotation-underline-03",
  },
];

export const AFA_MAX_FEEDBACK_REFERENCE = 2;

export const DISCUSSION_MODE = reflectKeys(["TOPIC_DEFAULT", "TOPIC_LIVE_CHAT", "TOPIC_LA"]);

export const MAX_CONVO_DURATION_IN_SECONDS = 1200;

export const MAXIMUM_RESOURCE_TAGGINGS = 50;

export const ANSWER_SIZE_INPUT = reflectKeys([
  "SIZE_S",
  "SIZE_M",
  "SIZE_1",
  "SIZE_2",
  "SIZE_3",
  "SIZE_4",
  "SIZE_5",
  "SIZE_6",
  "SIZE_7",
  "SIZE_8",
  "SIZE_9",
]);

// KAT
export const KEY_APPLICATION_OF_TECHNOLOGY = [
  {
    name: "Support Assessment for Learning",
    value: "SUPPORT_ASSESSMENT_FOR_LEARNING",
  },
  {
    name: "Foster Conceptual Change",
    value: "FOSTER_CONCEPTUAL_CHANGE",
  },
  {
    name: "Provide Differentiation",
    value: "PROVIDE_DIFFERENTIATION",
  },
  {
    name: "Facilitate Learning Together",
    value: "FACILITATE_LEARNING_TOGETHER",
  },
  {
    name: "Develop Metacognition",
    value: "DEVELOP_METACOGNITION",
  },
  {
    name: "Increase Motivation",
    value: "INCREASE_MOTIVATION",
  },
  {
    name: "Enable Personalisation",
    value: "ENABLE_PERSONALISATION",
  },
  {
    name: "Embed Scaffolding",
    value: "EMBED_SCAFFOLDING",
  },
] as const;

export const DEFAULT_MCQ_OPTIONS = 4;
